<template>
  <div class="rc-rfr-contacts-table" data-cy="rc-rfr-contacts-table">
    <div
      class="rc-rfr-contacts-table-header grid-control__container table-modifiers"
    >
      <TableSearch
        class="rc-rfr-contacts-search"
        :value="searchInput"
        placeholder="Search by Name or Email address"
        @searchSubmitted="onSearchSubmitted"
        data-cy="rc-rfr-contacts-search"
      />
      <div class="header-right-block">
        <GridActionsMenu
          @actionClicked="onActionClicked"
          data-cy="grid-actions"
        />
      </div>
    </div>
    <RcRfrContactsGrid
      :contacts="contacts"
      :loading="loading"
      :skip="skip"
      :pageSize="take"
      :total="totalContacts"
      :filter="filter"
      :sort="sortDescriptors"
      :getPropValues="getPropValuesAsync"
      ref="gridRef"
      @contactsChanged="onContactsChanged"
      @sortChanged="onSortChanged"
      @columnreorder="updateColumnMenuIcons"
      @columnssubmit="updateColumnMenuIcons"
    />
    <ColumnMenuIconHandler :filter="filter" ref="menuIconHandlerRef" />
  </div>
</template>

<script setup lang="ts">
import { api } from "@/api";
import { useMainStore } from "@/stores/useMainStore";
import { computed, onBeforeMount, ref } from "vue";
import RcRfrContactsGrid from "./RcRfrContactsGrid.vue";
import type {
  CompositeFilterDescriptor,
  FilterDescriptor,
  SortDescriptor,
} from "@progress/kendo-data-query";
import TableSearch from "@/shared/ui/inputs/TableSearch.vue";
import { CacheService } from "@/shared/services/CacheService";
import { createCacheKey } from "@/shared/helpers/cacheHelper";

import {
  valueFilterConvert,
  getValueFilterDistinct,
  FilterValuesTake,
  getJsonFilterString,
} from "@/shared/helpers/filtersHelper";
import type {
  ItemChangedOptions,
  ItemSortChangedOptions,
} from "@/Domain/ItemChangedOptions";
import { handleResponsePromiseAsync } from "@/shared/helpers/apiHelper";
import type { ValueFieldConvertOption } from "@/Domain/ValueFieldConvertOption";
import ColumnMenuIconHandler from "@/shared/ui/filters/ColumnMenuIconHandler.vue";
import { GridOptionsService } from "@/shared/services/GridOptionsService";
import { GridMenuActionType } from "@/Domain/GridMenuActionType";
import GridActionsMenu from "@/shared/ui/inputs/GridActionsMenu.vue";
import type { Contact } from "@/Domain/Contact";
import type { GetRcRfrContactsWithFilteringRequest } from "@/api/rcRfrContacts";
import { EnumPersonJobStatus } from "@/Domain/EnumPersonJobStatus";
import type { GridColumnPropsExtended } from "@/Domain/GridColumnPropsExtended";

const main = useMainStore();

const tableName = "RcRfrContactsTable";

const defaultTake = 25;

const searchInput = ref<string>("");
const contacts = ref<Contact[]>([]);
const totalContacts = ref<number>(0);
const loading = ref<boolean>(false);
const skip = ref<number>(0);
const filter = ref<CompositeFilterDescriptor>(
  GridOptionsService.loadGridFilter(tableName)
);
const take = ref<number>(defaultTake);
const sortField = ref<string>("");
const sortAscending = ref<boolean>(false);

const gridRef = ref<InstanceType<typeof RcRfrContactsGrid>>();
const menuIconHandlerRef = ref<InstanceType<
  typeof ColumnMenuIconHandler
> | null>(null);

const sortDescriptors = computed(() => {
  return (
    sortField.value
      ? [
          {
            field: sortField.value,
            dir: sortAscending.value ? "asc" : "desc",
          },
        ]
      : []
  ) as SortDescriptor[];
});

const valueFiltersConvertOptions = [
  {
    column: "Status",
    convert: (value: number) => {
      return EnumPersonJobStatus[value];
    },
    convertBack: (value: string) => {
      return EnumPersonJobStatus[value as keyof typeof EnumPersonJobStatus];
    },
  }
] as ValueFieldConvertOption[];

const getContactsAsync = async function (
  filter: CompositeFilterDescriptor | null = null,
  valuesForFieldOnly: string = ""
) {
  const requestParams = {
    userLogin: main.userLogin,
    searchString: searchInput.value || "",
    filters: getJsonFilterString(filter, valueFiltersConvertOptions),
    skip: skip.value,
    take: take.value,
    sortField: sortField.value,
    ascending: sortAscending.value,
  } as GetRcRfrContactsWithFilteringRequest;

  const endpoint = "GetRcRfrContactsWithFiltering";

  if (!valuesForFieldOnly) return api.get(endpoint, requestParams);

  requestParams.valuesForFieldOnly = valuesForFieldOnly;
  requestParams.take = FilterValuesTake;
  requestParams.skip = 0;

  return CacheService.getResponseAsync(
    createCacheKey(requestParams, endpoint),
    () => api.get(endpoint, requestParams)
  );
};

const clearFilters = function () {
  searchInput.value = "";
  filter.value = {
    logic: "and",
    filters: [{} as FilterDescriptor],
  };

  sortField.value = "";
  sortAscending.value = false;
  updateContactsAsync();
};

const onSearchSubmitted = function (searchValue: string) {
  searchInput.value = searchValue;
  skip.value = 0;
  updateContactsAsync(filter.value);
};

const onContactsChanged = function (options: ItemChangedOptions) {
  // Kendo Grid sending two duplicate events on filter change
  // If filterEvent.filter is null, it's the second event and we should ignore it.
  // FilterEvent is null when user change page size or page number
  // And in that case only one event is sent
  if (options.filterEvent) {
    if (!options.filterEvent.filter) return;
    filter.value.filters = options.filterEvent.filter.filters;
  }
  if (take.value != options.take) {
    take.value = options.take;
    GridOptionsService.savePageSizeAsync(tableName, options.take);
  }
  skip.value = options.skip;
  updateContactsAsync(filter.value);
};

const onSortChanged = function (options: ItemSortChangedOptions) {
  sortField.value = options.field;
  sortAscending.value = options.ascending;
  skip.value = 0;
  updateContactsAsync(filter.value);
};

const updateContactsAsync = async function (
  filter: CompositeFilterDescriptor | null = null
) {
  if (loading.value) return;
    loading.value = true;

  await handleResponsePromiseAsync(
    getContactsAsync(filter),
    (data) => {
      contacts.value = data.ListItems;
      totalContacts.value = data.TotalItemsInList;
    },
    "RcRfrContactsTable.vue",
    "updateContactsAsync"
  );

  loading.value = false;
};

const getPropValuesAsync = async function (
  column: GridColumnPropsExtended
) {
  let result: Contact[] = [];
  if (!column.field) return result;

  await handleResponsePromiseAsync(
    getContactsAsync(filter.value, column.field),
    (data) => {
      result = data.ListItems;
      valueFilterConvert(valueFiltersConvertOptions, column.field!, result);
      result = getValueFilterDistinct(column.field!, result);
    },
    "RcRfrContactsTable.vue",
    "getProductPropValuesAsync"
  );
  return result;
};

const resetPagination = function () {
  if (take.value === defaultTake) return;

  skip.value = 0;
  take.value = defaultTake;
  GridOptionsService.clearPageSizeAsync(tableName);
  updateContactsAsync(filter.value);
};

const updateColumnMenuIcons = function () {
  setTimeout(() => {
    menuIconHandlerRef.value?.updateColumnMenuIcons();
  }, 100);
};

const onActionClicked = function (actionType: GridMenuActionType) {
  switch (actionType) {
    case GridMenuActionType.ClearAllFilters:
      clearFilters();
      break;
    case GridMenuActionType.ResetPagination:
      resetPagination();
      break;
    case GridMenuActionType.ResetColumnOrder:
      updateColumnMenuIcons();
      break;
    case GridMenuActionType.ResetAllSettings:
      resetPagination();
      updateColumnMenuIcons();
      break;
  }
  if (gridRef.value) gridRef.value.executeAction(actionType);
};

onBeforeMount(async () => {
  const pageSize = await GridOptionsService.getPageSizeAsync(tableName);
  if (pageSize) take.value = pageSize;
  updateContactsAsync(filter.value);
});
</script>

<style lang="scss">
.rc-rfr-contacts-table {
  margin: 0 24px 24px;

  .rc-rfr-contacts-table-header {
    display: flex;
    gap: 12px;

    .rc-rfr-contacts-search {
        width: 400px;
        margin-right: 16px;
    }

    .header-right-block {
      flex: 1;
      display: flex;
      align-items: center;
      justify-content: flex-end;
    }
  }
}
</style>
